import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { canEdit } from "../lib/permissions";
import { api } from "../lib/api";
import { AllergiesPanel } from "./AllergiesPanel";
import ProblemList from "./medical/ProblemList";
import MedicationList from "./medical/MedicationList";
import PrescriptionList from "./medical/PrescriptionList";
import LabOrderList from "./medical/LabOrderList";
import FamilyHealthHistory from "./medical/FamilyHealthHistory";
import ImmunizationList from "./medical/ImmunizationList";
import MedicalReferralList from "./medical/MedicalReferralList";
import MedicalNotes from "./medical/MedicalNotes";
import VitalSigns from "./medical/VitalSigns";
import CareTeam from "./medical/CareTeam";
import AdvanceDirectives from "./medical/AdvanceDirectives";
import PreventiveCare from "./medical/PreventiveCare";
import MetabolicMonitoring from "./medical/MetabolicMonitoring";
import SurgicalHistory from "./medical/SurgicalHistory";
import ReproductiveHealth from "./medical/ReproductiveHealth";
import MedicalDevices from "./medical/MedicalDevices";
import { NewPill } from "../components/NewPill";
import {
  ListChecks, Pill, ShieldAlert, FlaskConical, Users, Syringe, ArrowRightLeft, NotebookPen,
  Activity, Stethoscope, FileSignature, ClipboardCheck, Zap, Scissors, HeartHandshake, Cpu, FileText,
} from "lucide-react";

const SUBS = [
  { key: "problems", label: "Master Problem List", icon: ListChecks, testId: "med-tab-problems" },
  { key: "medications", label: "Master Medication List", icon: Pill, testId: "med-tab-medications" },
  { key: "prescriptions", label: "Prescriptions", icon: FileText, testId: "med-tab-prescriptions", fresh: "prescriptions" },
  { key: "allergies", label: "Allergy Information", icon: ShieldAlert, testId: "med-tab-allergies" },
  { key: "lab_orders", label: "Lab Orders", icon: FlaskConical, testId: "med-tab-lab-orders" },
  { key: "family", label: "Family Health History", icon: Users, testId: "med-tab-family" },
  { key: "immunizations", label: "Immunizations", icon: Syringe, testId: "med-tab-immunizations" },
  { key: "referrals", label: "Medical Referrals", icon: ArrowRightLeft, testId: "med-tab-referrals" },
  { key: "notes", label: "Medical Notes", icon: NotebookPen, testId: "med-tab-notes" },
  { key: "vitals", label: "Vital Signs", icon: Activity, testId: "med-tab-vitals" },
  { key: "care_team", label: "Care Team", icon: Stethoscope, testId: "med-tab-care-team" },
  { key: "directives", label: "Advance Directives", icon: FileSignature, testId: "med-tab-directives" },
  { key: "preventive", label: "Preventive Care", icon: ClipboardCheck, testId: "med-tab-preventive" },
  { key: "metabolic", label: "Metabolic Monitoring", icon: Zap, testId: "med-tab-metabolic" },
  { key: "surgical", label: "Surgical History", icon: Scissors, testId: "med-tab-surgical" },
  { key: "reproductive", label: "Reproductive Health", icon: HeartHandshake, testId: "med-tab-reproductive" },
  { key: "devices", label: "Medical Devices", icon: Cpu, testId: "med-tab-devices" },
];

// Six-group Medical bottom nav (mirrors the top-strip grouped pattern for consistency).
const SUB_GROUPS = [
  { key: "basic", label: "Basic", tabs: ["problems", "allergies", "immunizations", "family"] },
  { key: "meds", label: "Meds", tabs: ["medications", "prescriptions", "vitals"], fresh: "prescriptions" },
  { key: "assessments", label: "Assessments", tabs: ["metabolic", "preventive"] },
  { key: "orders", label: "Orders", tabs: ["lab_orders", "referrals"] },
  { key: "notes", label: "Notes", tabs: ["notes", "care_team", "directives"] },
  { key: "advanced", label: "Advanced", tabs: ["surgical", "reproductive", "devices"] },
];

function groupForKey(key) {
  const g = SUB_GROUPS.find((gr) => gr.tabs.includes(key));
  return g ? g.key : SUB_GROUPS[0].key;
}

export function ClientMedical({ client, initialSub = "problems", onSummary }) {
  const { user } = useAuth();
  const editable = canEdit(user, "clinical");
  const [sub, setSub] = useState(initialSub);
  const [activeGroup, setActiveGroup] = useState(() => groupForKey(initialSub));
  const [dict, setDict] = useState(null);
  const [summary, setSummary] = useState(null);
  const [noteCount, setNoteCount] = useState(0);

  useEffect(() => {
    api.get("/medical/dictionaries").then((r) => setDict(r.data)).catch(() => {});
  }, []);
  const loadSummary = () => api.get(`/clients/${client.id}/medical/summary`).then((r) => { setSummary(r.data); onSummary?.(r.data); }).catch(() => {});
  useEffect(() => {
    if (!client?.id) return;
    loadSummary();
    api.get(`/clients/${client.id}/chart-notes`, { params: { category: "Medical" } })
      .then((r) => setNoteCount(Array.isArray(r.data) ? r.data.length : 0)).catch(() => setNoteCount(0));
  }, [client?.id, sub]); // eslint-disable-line

  const badgeFor = (key) => {
    if (key === "notes") return noteCount;
    return summary?.counts?.[
      key === "problems" ? "problems" : key === "medications" ? "medications"
      : key === "allergies" ? "allergies" : key === "lab_orders" ? "lab_orders"
      : key === "family" ? "family_history" : key === "immunizations" ? "immunizations"
      : key === "prescriptions" ? "prescriptions" : key === "referrals" ? "medical_referrals" : ""
    ];
  };

  const groupSubs = SUB_GROUPS.find((g) => g.key === activeGroup)?.tabs || [];
  return (
    <div className="space-y-4" data-testid="client-medical-panel">
      <div className="space-y-2">
        <div className="flex items-center gap-1 overflow-x-auto rounded-lg bg-[#F1EEE7] p-1" data-testid="medical-subtab-groups">
          {SUB_GROUPS.map((g) => {
            const isActive = g.key === activeGroup;
            return (
              <button key={g.key} type="button" data-testid={`med-group-${g.key}`}
                onClick={() => { setActiveGroup(g.key); setSub(g.tabs[0]); }}
                className={`flex-1 rounded-md px-3 py-1.5 text-xs font-bold uppercase tracking-wide transition-colors ${isActive ? "bg-[#4E342E] text-white shadow-sm" : "text-[#7B6157] hover:text-[#4E342E]"}`}>
                <span className="inline-flex items-center gap-1.5">{g.label}{g.fresh && <NewPill feature={g.fresh} />}</span>
              </button>
            );
          })}
        </div>
        <div className="flex flex-wrap items-center gap-1 rounded-2xl border border-[#EFEBE0] bg-white p-1" data-testid="medical-subtabs">
          {groupSubs.map((key) => {
            const s = SUBS.find((x) => x.key === key);
            if (!s) return null;
            const Icon = s.icon;
            const badge = badgeFor(s.key);
            const active = sub === s.key;
            return (
              <button key={s.key} data-testid={s.testId} onClick={() => setSub(s.key)}
                className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-bold transition-colors ${
                  active ? "bg-[#4E342E] text-white" : "text-[#5D4037] hover:bg-[#F5F2EB]"}`}>
                <Icon size={13} /> {s.label}{s.fresh && <NewPill feature={s.fresh} />}
                {typeof badge === "number" && badge > 0 && (
                  <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-black ${
                    active ? "bg-white/20 text-white" : "bg-[#EFEBE0] text-[#6D4C41]"}`}>{badge}</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="rounded-2xl border border-[#EFEBE0] bg-white p-5">
        {sub === "problems" && <ProblemList client={client} editable={editable} onChanged={loadSummary} />}
        {sub === "medications" && <MedicationList client={client} editable={editable} />}
        {sub === "prescriptions" && <PrescriptionList client={client} />}
        {sub === "allergies" && <AllergiesPanel client={client} />}
        {sub === "lab_orders" && <LabOrderList client={client} editable={editable} dict={dict} />}
        {sub === "family" && <FamilyHealthHistory client={client} editable={editable} dict={dict} />}
        {sub === "immunizations" && <ImmunizationList client={client} editable={editable} dict={dict} />}
        {sub === "referrals" && <MedicalReferralList client={client} editable={editable} dict={dict} />}
        {sub === "notes" && <MedicalNotes client={client} />}
        {sub === "vitals" && <VitalSigns client={client} />}
        {sub === "care_team" && <CareTeam client={client} />}
        {sub === "directives" && <AdvanceDirectives client={client} />}
        {sub === "preventive" && <PreventiveCare client={client} />}
        {sub === "metabolic" && <MetabolicMonitoring client={client} />}
        {sub === "surgical" && <SurgicalHistory client={client} />}
        {sub === "reproductive" && <ReproductiveHealth client={client} />}
        {sub === "devices" && <MedicalDevices client={client} />}
      </div>
    </div>
  );
}

export default ClientMedical;
